import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useState } from "react";

interface CreateStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (data: { name: string; description: string; color: string }) => Promise<void>;
  isCreating: boolean;
}

export function CreateStatusDialog({
  open,
  onOpenChange,
  onConfirm,
  isCreating,
}: CreateStatusDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("#3B82F6");

  const handleSubmit = async () => {
    if (!name.trim()) return;
    
    await onConfirm({ name, description, color });
    
    // Reset form
    setName("");
    setDescription("");
    setColor("#3B82F6");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Status</DialogTitle>
          <DialogDescription>
            Add a new status to your claim workflow. You can add tasks to it after creation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="status-name">Status Name *</Label>
            <Input
              id="status-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Under Review"
              disabled={isCreating}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="status-description">Description</Label>
            <Input
              id="status-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              disabled={isCreating}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="status-color">Color</Label>
            <div className="flex gap-2 items-center">
              <Input
                id="status-color"
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-20 h-10 cursor-pointer"
                disabled={isCreating}
              />
              <Input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                placeholder="#3B82F6"
                className="flex-1"
                disabled={isCreating}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isCreating || !name.trim()}
          >
            {isCreating && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            Create Status
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
